import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useGetCompany, useGetInherentRisk } from '@/api/company.hook'
import { ControlWithSignals, useListControls } from '@/api/control.hook'
import { Company, GetInherentRiskRequestSchema } from '@/gen/inventory/v1/company_service_pb'
import {
  Control,
  ControlInventoryService,
  ControlTimelineItem,
  ListControlsRequestSchema,
} from '@/gen/inventory/v1/control_service_pb'
import { GetControlResponse } from '@/gen/inventory/v1/control_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { GetSignalResponse, SignalService } from '@/gen/inventory/v1/signal_service_pb'

import { sessionTokenInterceptor } from '@/lib/auth/session-token-interceptor'
import { getConnectTransport } from '@/lib/connect/connect-transport'

const controlInventoryClient = createClient(
  ControlInventoryService,
  getConnectTransport({
    interceptors: [sessionTokenInterceptor],
  }),
)

const signalServiceClient = createClient(
  SignalService,
  getConnectTransport({
    interceptors: [sessionTokenInterceptor],
  }),
)

export const ControlDataQueryKey = 'controlsData'

export type ControlWithSignalsAndTimeline = ControlWithSignals & {
  timeline: ControlTimelineItem[]
}

export const useFetchAllControls = (companyId: string, controls: Control[], enabled: boolean) => {
  const query = useQuery({
    enabled,
    refetchOnWindowFocus: false,
    queryKey: [ControlDataQueryKey, companyId],
    queryFn: async () => {
      if (controls.length === 0) {
        return []
      }

      const controlsResponses = await Promise.allSettled(
        controls.map((control) =>
          controlInventoryClient.getControl({
            controlId: control.id,
            staticOutput: true,
          }),
        ),
      )

      const successfulControls = controlsResponses
        .filter(
          (result): result is PromiseFulfilledResult<GetControlResponse> =>
            result.status === 'fulfilled',
        )
        .map((result) => result.value)

      const controlWithAlertResponses = await Promise.allSettled(
        successfulControls.map(async (control) => {
          const [signalResponses, timelineResponse] = await Promise.allSettled([
            Promise.allSettled(
              (control.signalIds || []).map((id) =>
                signalServiceClient.getSignal({
                  id,
                  staticOutput: true,
                }),
              ),
            ),
            controlInventoryClient.getControlTimeline({
              controlId: control.control?.id || '',
            }),
          ])

          const successfulSignals =
            signalResponses.status === 'fulfilled'
              ? signalResponses.value
                  .filter(
                    (result): result is PromiseFulfilledResult<GetSignalResponse> =>
                      result.status === 'fulfilled',
                  )
                  .map((result) => result.value)
              : []

          const timelineItems =
            timelineResponse.status === 'fulfilled' ? timelineResponse.value.items || [] : []

          return {
            ...control,
            signals: successfulSignals,
            timeline: timelineItems,
          } as ControlWithSignalsAndTimeline
        }),
      )

      return controlWithAlertResponses
        .filter(
          (result): result is PromiseFulfilledResult<ControlWithSignalsAndTimeline> =>
            result.status === 'fulfilled',
        )
        .map((result) => result.value)
    },
  })

  return query
}

export const useFetchCompanyData = ({
  companyId,
  assessmentId,
  enabled,
}: {
  companyId: Company['id']
  assessmentId?: OngoingAssessment['id']
  enabled: boolean
}) => {
  const {
    data: company,
    isFetching: companyLoading,
    isError: companyError,
  } = useGetCompany(companyId, enabled)
  const { data: inherentRiskGroups, isFetching: inherentRiskGroupsLoading } = useGetInherentRisk(
    create(GetInherentRiskRequestSchema, { companyId }),
    enabled,
  )
  const { data: controls, isFetching: controlsLoading } = useListControls({
    ...create(ListControlsRequestSchema, { assessmentId: assessmentId ?? '' }),
    enabled: enabled && !!assessmentId,
  })

  const activeControls = useMemo(
    () => controls?.filter(({ isEnabled }) => isEnabled) || [],
    [controls],
  )
  const { data: controlsData, isFetching: controlsDataLoading } = useFetchAllControls(
    companyId,
    activeControls,
    enabled,
  )

  const loading =
    companyLoading || inherentRiskGroupsLoading || controlsLoading || controlsDataLoading
  const isError = companyError

  return {
    loading,
    isError,
    data: {
      company,
      inherentRiskGroups,
      controlsData,
    },
  }
}
